import { Inject, Injectable } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StateService } from '@uirouter/core';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    switchMap,
    tap
} from 'rxjs/operators';
import * as fromAuth from '../auth/store/auth.actions';
import * as fromSettings from '../company-setting';
import * as fromDataServices from '../data-services';
import * as fromLogo from '../logo/store/logo.actions';
import * as fromLookups from '../lookups';
import {
    LoadAuthorizationClasses,
    LoadAuthorizationTypes,
    LoadContractTypes,
    LoadDaysOfWeek,
    LoadDirectReports,
    LoadIndirectTypes,
    LoadPayrollItems,
    LoadServiceItems,
    LoadStates,
    LoadUserGroups,
    LookupActionTypes,
    SetDirectReports
} from '../lookups/store/actions';
import * as fromReports from '../reporting/store';
import * as fromShared from '../shared';
import { ResponseStatus } from '../shared';
import { LoggingService } from '../shared/services/logging/logging.service';
import * as fromUser from '../user-info/store/user.actions';
import * as fromUserRole from '../user-role/store/actions';
import * as fromRoot from './actions';
import { SetRedirect } from './core';
import { VersionService } from '../app.service';
import * as fromUserSetting from '../user-setting';
import { CategoryService } from '../shared/services/category.service';
@Injectable()
export class EfaactEffects {
    contractDataService: EntityCollectionService<fromDataServices.Contract.Contract>;

    constructor(
        public entityServices: EntityServices,
        private actions$: Actions,
        private ss: StateService,
        private log: LoggingService,
        private versionService: VersionService,
        private categoryService: CategoryService
    ) {
        this.contractDataService =
            entityServices.getEntityCollectionService<fromDataServices.Contract.Contract>(
                fromDataServices.Contract.entityName
            );
    }

    Login$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromRoot.LoginAction>(fromRoot.ActionTypes.Login),
            tap(action =>
                this.log.debug({
                    type: 'rooteffect processing LoginAction',
                    action
                })
            ),
            // concatmap processes items in sequence
            concatMap(action => [
                new fromAuth.RememberMe(action.payload.req),
                new fromAuth.LoginSuccess(action.payload.resp)
            ])
        )
    );
    //  Just using this to transform a logout to a sequence of actions (splitter pattern)
    Logout$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromRoot.ActionTypes.Logout),
                map(() => new fromRoot.ResetAction()),
                tap(x => this.ss.go('main')),
                tap(() => this.categoryService.clearSelectedCategory())
            ),
        { dispatch: false },
    );
    LoginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromAuth.LoginSuccess>(
                fromAuth.AuthActionTypes.LoginSuccess
            ),
            tap(() =>
                this.contractDataService.getWithQuery({
                    Activeonly: false as any
                })
            ),
            concatMap(() => [
                new fromLookups.LoadPayrollItems([]),
                new fromLookups.LoadPayrollFrequency(),
                new fromUser.LoadUserInfo(),
                new fromUserRole.LoadUserRoles(),
                new LoadAuthorizationTypes(),
                new LoadUserGroups(),
                new LoadServiceItems(),
                new LoadAuthorizationClasses(),
                new LoadContractTypes(),
                new LoadIndirectTypes(),
                new LoadStates(),
                new fromLookups.LoadSplitday(),
                new LoadDaysOfWeek(),
                new fromLookups.LoadEntityFilter('User'),
                fromLookups.ScheduledDateActions.getScheduledDates({
                    ShowAll: false
                }),
                // fromLookups.ContractActions.getContracts(), // this is loaded here for reports slide out
                new fromLookups.LoadTimeZones(),
                new fromLookups.LoadUserTimeZones(),
                // new fromManageUsers.LoadLogins({
                //     userType: 7,
                //     showInactive: false,
                //     payrollItems: null,
                //     serviceItems: null,
                //     costCenters: null
                // }),
                new fromShared.Errors.ClearErrors(),
                new fromLookups.LoadColors(),
                new fromLookups.LoadMobileProviders(),
                fromSettings.getCompanySetting({
                    SettingType: 'QuickbooksOnline'
                }),
                fromSettings.getCompanySetting({
                    SettingType: fromSettings.CompanySettingEnum.ShowAIHelpButton
                }),
                fromReports.setPopupBlocker({ value: false }),
                fromLogo.getLogo({ UploadType: 'Web Logo' })
            ])
        )
    );

    // INFO: put anything that is required to be loaded for the logged in user in here
    SetUserInfo$ = createEffect(() =>
         this.actions$.pipe(
            ofType<fromUser.SetUserInfo>(fromUser.UserActionTypes.SetUserInfo),
            mergeMap(action => [
                // new LoadTasks(), //removed this call from here and put it only where needed
                new fromUser.LoadUserPermissions(),
                fromUserSetting.getUserSettings({
                    EmployeeListID: action.payload.EmployeeListID,
                    EmpSettingID: fromUserSetting.UserSettingType.TemporaryPassword
                }),
                fromUserSetting.getUserSettings({
                    EmployeeListID: action.payload.EmployeeListID,
                    EmpSettingID: fromUserSetting.UserSettingType.UserAIHelpButton
                }),
                new fromUser.LoadUserColors(action.payload.EmployeeListID),
                //new LoadDirectReports({ user: action.payload, activeOnly: false }), //removed this call from here and put it only where needed
                fromReports.getReports({
                    // could maybe be removed and added to the LoginSuccess action
                    EmployeeListID: action.payload.EmployeeListID
                })
            ])
        )
    );

    // @Effect() DirectReports$ = this.actions$.pipe(
    //     ofType<SetDirectReports>(LookupActionTypes.SetDirectReports),
    //     map(
    //         a =>
    //             new LoadPayrollItems([
    //                 ...((a.payload && a.payload.map(dr => dr.EmployeeListID)) ||
    //                     [])
    //             ])
    //     ),
    //     catchError((e, c) => {
    //         this.log.error({ e, c });
    //         return c;
    //     })
    // );

    Errors$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromShared.Errors.SetErrors>(
                fromShared.Errors.ErrorsSidenavActionTypes.SetErrors
            ),
            filter(action =>
                action.payload.some(
                    x =>
                        !(x.MsgEntityID && x.MsgProcessID) &&
                        x.StatusCode === ResponseStatus.Failed
                )
            ),
            map(() => new SetRedirect(null))
        )
    );
    version$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromRoot.GetVersionAction>(fromRoot.ActionTypes.GetVersion),
            /** An EMPTY observable only emits completion. Replace with your own observable stream */
            switchMap(() =>
                this.versionService.getVersion().pipe(
                    // tap(data => this.log.verbose({ type: 'version$:resp', data })),
                    map(data => new fromRoot.VersionSuccess({ version: data })),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
